export const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '')

const truthyStatuses = new Set(['1', 'true', 'yes', 'saved'])
const falseyStatuses = new Set(['0', 'false', 'no', 'unsaved', 'removed'])

export const hasValue = (value) => {
  const normalized = String(value ?? '').trim()
  return normalized !== '' && normalized !== '0'
}

export const getFlyerBoardId = (item = {}, fallback = {}) => firstValue(
  item.board_id,
  item.boardId,
  item.boardid,
  item.saved_board_id,
  item.savedBoardId,
  item.saved_id,
  item.savedId,
  item.board?.id,
  item.board?.board_id,
  fallback.boardId,
  fallback.board_id,
  fallback.saved_board_id,
  ''
)

export const normalizeFlyerSaved = (item = {}, fallback = {}) => {
  const status = firstValue(
    item.saved,
    item.is_saved,
    item.save_status,
    item.saved_status,
    item.flyer_saved,
    item.is_flyer_saved,
    fallback.saved,
    fallback.is_saved,
    fallback.save_status,
    fallback.saved_status,
    ''
  )
  const normalizedStatus = String(status ?? '').trim().toLowerCase()
  if (truthyStatuses.has(normalizedStatus)) return true
  if (hasValue(getFlyerBoardId(item, fallback))) return true
  if (falseyStatuses.has(normalizedStatus)) return false
  return false
}

export const normalizeHexColor = (value, fallback = '2563eb') => {
  const normalized = String(value || fallback).replace('#', '').trim()
  return /^[0-9a-f]{6}$/i.test(normalized) ? normalized : fallback
}

export const buildImageUrl = (value) => {
  const image = Array.isArray(value) ? value[0] : value
  if (!image) return ''
  if (/^https?:\/\//i.test(image)) return String(image).replace(/^http:\/\//i, 'https://')
  return `https://test.amplifierapp.xyz/${String(image).replace(/^\/+/, '')}`
}

const extractImageValues = (value) => {
  if (value === undefined || value === null || value === '') return []
  if (Array.isArray(value)) return value.flatMap(extractImageValues)
  if (typeof value === 'object') {
    const nestedImage = firstValue(
      value.url,
      value.image_url,
      value.image,
      value.path,
      value.file,
      value.file_name,
      value.src
    )
    return nestedImage !== undefined ? extractImageValues(nestedImage) : Object.values(value).flatMap(extractImageValues)
  }

  const image = String(value).trim()
  if (!image || ['0', 'null', 'undefined'].includes(image.toLowerCase())) return []
  if (image.startsWith('[') || image.startsWith('{')) {
    try {
      return extractImageValues(JSON.parse(image))
    } catch {
      // Continue with the original value when it is not valid JSON.
    }
  }
  if (!image.startsWith('data:') && image.includes(',')) {
    return image.split(',').flatMap(extractImageValues)
  }
  return [image]
}

const normalizeImageValues = (value) => extractImageValues(value).map(buildImageUrl).filter(Boolean)

export const extractFlyerDetail = (payload) => {
  if (!payload || typeof payload !== 'object') return null

  const detailKeys = ['flyer', 'detail', 'data', 'result', 'list']
  const looksLikeFlyer = (value) => value && typeof value === 'object' && !Array.isArray(value) && [
    'id',
    'flyer_id',
    'f_id',
    'description',
    'flyer_type',
    'image',
    'image1',
    'images',
    'flyer_images',
    'business_name'
  ].some((key) => Object.prototype.hasOwnProperty.call(value, key))

  const findFlyer = (value, depth = 0) => {
    if (!value || typeof value !== 'object' || depth > 6) return null
    if (Array.isArray(value)) {
      for (const item of value) {
        const flyer = findFlyer(item, depth + 1)
        if (flyer) return flyer
      }
      return null
    }
    if (looksLikeFlyer(value)) return value

    for (const key of detailKeys) {
      const flyer = findFlyer(value[key], depth + 1)
      if (flyer) return flyer
    }
    return null
  }

  return findFlyer(payload)
}

export const normalizeFlyerDetail = (item = {}, fallback = {}) => {
  const collectionImages = [
    ...normalizeImageValues(item.images),
    ...normalizeImageValues(item.flyer_images)
  ]
  const explicitImageUrls = [
    firstValue(item.image, item.flyer_image, item.flyer_img, item.image_url, item.photo, item.thumbnail),
    item.image1,
    item.image2,
    item.image3,
    item.image4
  ].map((image) => normalizeImageValues(image)[0] || '')
  const seenExplicitImages = new Set()
  const explicitImageSlots = explicitImageUrls.map((image) => {
    if (!image || seenExplicitImages.has(image)) return ''
    seenExplicitImages.add(image)
    return image
  })
  const slottedImageUrls = explicitImageSlots.some(Boolean)
    ? [...explicitImageSlots]
    : Array.from({ length: 5 }, (_, index) => collectionImages[index] || '')
  const slottedImages = new Set(slottedImageUrls.filter(Boolean))

  collectionImages.forEach((image) => {
    if (slottedImages.has(image)) return
    const availableSlot = slottedImageUrls.findIndex((slotImage) => !slotImage)
    if (availableSlot < 0) return
    slottedImageUrls[availableSlot] = image
    slottedImages.add(image)
  })

  const imageSlots = slottedImageUrls
    .map((url, slotIndex) => ({ url, slotIndex }))
    .filter((image) => image.url)
  const normalizedImages = imageSlots.map((image) => image.url)
  const category = firstValue(item.content_type_name, item.category_name, item.cat_name, item.flyer_category, fallback.category_name, fallback.category, 'Flyer')
  const rawDate = firstValue(item.date, item.checkin_date, item.created_date, item.created_at, item.start_date, item.updated_at, fallback.date, '')
  const postedAt = firstValue(item.posted_at, item.post_date, item.checkin_date, item.created_at, item.created_time, item.updated_at, item.created_date, fallback.postedAt, fallback.created_at, rawDate)
  const boardId = getFlyerBoardId(item, fallback)
  const colorCode = normalizeHexColor(
    firstValue(item.param_code, item.paramCode, item.color_code, item.colorCode, item.code, item.color, fallback.param_code, fallback.color_code, fallback.color, '2563eb')
  )

  return {
    ...fallback,
    ...item,
    id: firstValue(item.id, item.flyer_id, item.f_id, fallback.id, ''),
    flyer_id: firstValue(item.flyer_id, item.id, item.f_id, fallback.flyer_id, fallback.id, ''),
    userId: firstValue(
      item.user_id,
      item.userId,
      item.userid,
      item.uid,
      item.created_by,
      item.created_by_id,
      item.owner_id,
      item.author_id,
      fallback.userId,
      fallback.user_id,
      ''
    ),
    username: firstValue(item.user_username, item.username, item.user_name, item.name, fallback.username, ''),
    title: firstValue(item.title, item.flyer_title, item.heading, fallback.title, ''),
    category,
    category_name: category,
    categoryId: firstValue(item.category_id, item.category, fallback.categoryId, ''),
    contentTypeId: firstValue(item.content_type, item.content_type_id, item.category_id, fallback.contentTypeId, ''),
    flyerLocation: firstValue(item.flyerLocation, item.flyer_location, item.location, item.location_name, item.address, item.flyer_address, ''),
    location: firstValue(item.location, item.location_name, item.city, item.address, fallback.location, ''),
    address: firstValue(item.address, item.flyer_address, item.location_name, item.location, fallback.address, ''),
    date: rawDate,
    postedAt,
    status: firstValue(item.status, item.flyer_status, fallback.status, ''),
    colorCode,
    param_code: colorCode,
    image: firstValue(normalizedImages[0], ''),
    images: normalizedImages,
    imageSlots,
    author: firstValue(item.author, item.user_name, item.name, item.user_username, item.username, fallback.author, 'NetFlyr'),
    authorAvatar: buildImageUrl(firstValue(item.user_img, item.author_img, item.profile_image, item.avatar, fallback.authorAvatar, '')),
    description: firstValue(item.description, item.flyer_description, item.desc, item.details, item.content, fallback.description, ''),
    likesCount: Number(firstValue(item.likes_count, item.likesCount, item.like_count, fallback.likesCount, 0)),
    viewsCount: Number(firstValue(item.views_count, item.viewsCount, item.view_count, fallback.viewsCount, 0)),
    commentsCount: Number(firstValue(item.comments_count, item.commentsCount, item.comment_count, item.review_count, fallback.commentsCount, 0)),
    saved: normalizeFlyerSaved(item, fallback),
    liked: ['1', 'true', 'yes', 'liked'].includes(String(firstValue(item.liked, item.is_liked, item.like_status, item.liked_status, fallback.liked, '')).toLowerCase()),
    startsDate: firstValue(item.startsDate, item.start_date, item.offer_start_date, fallback.startsDate, ''),
    endsDate: firstValue(item.endsDate, item.end_date, item.offer_end_date, fallback.endsDate, ''),
    boardId,
    board_id: boardId,
    board: firstValue(item.board_name, item.board, fallback.board, ''),
    contentType: firstValue(item.content_type_name, item.contentType, item.category_name, item.cat_name, fallback.contentType, category),
    flyer_type: firstValue(item.flyer_type, item.flyerType, fallback.flyer_type, fallback.flyerType, 'flyer'),
    review_count: firstValue(item.review_count, item.rating, fallback.review_count, fallback.rating, ''),
    review_comment: firstValue(item.review_comment, item.comments, item.review, fallback.review_comment, fallback.comments, fallback.review, ''),
    business_name: firstValue(item.business_name, item.businessName, item.s_business, fallback.business_name, fallback.businessName, fallback.s_business, ''),
    business_adress: firstValue(
      item.business_adress,
      item.business_address,
      item.businessAddress,
      item.s_address,
      fallback.business_adress,
      fallback.business_address,
      fallback.businessAddress,
      fallback.s_address,
      ''
    ),
    business_lat: firstValue(item.business_lat, item.businessLatitude, item.business_latitude, item.s_latit, fallback.business_lat, fallback.businessLatitude, fallback.s_latit, ''),
    business_long: firstValue(item.business_long, item.businessLong, item.business_longitude, item.s_longit, fallback.business_long, fallback.businessLong, fallback.s_longit, '')
  }
}
